import { computed } from 'vue';
import type { UnitType } from '../intefaces/unit_type';
import type { StructureType } from '../intefaces/structure_type';

export default function useUnitLogic(structure: StructureType) {
  const selected_units = computed(() => structure.units || []);
  const selected_uniq_keys = computed(() =>
    selected_units.value.map(u => u.uniq_key).filter(Boolean)
  );

  function max_quantity(unit: UnitType): number {
    const hero_unit = structure.hero?.available_units?.find(u => u.tech_name === unit.tech_name);

    // Find all parent units that provide this subunit
    const parents = selected_units.value.filter(u =>
      u.available_units?.some(sub => sub.tech_name === unit.tech_name)
    );

    let total_from_parents = 0;
    parents.forEach(parent => {
      const parent_multiplier = parent.available_units?.find(
        sub => sub.tech_name === unit.tech_name
      )?.quantity || 0;
      total_from_parents += parent.quantity * parent_multiplier;
    });

    return total_from_parents + (hero_unit?.quantity || 0);
  }

  function update_unit_quantity(unit: UnitType, delta: number): void {
    if (!structure) return;

    const target_unit = selected_units.value.find(u => u.tech_name === unit.tech_name);
    const was_decreased = target_unit && delta < 0;
    const was_removed = target_unit && target_unit.quantity + delta <= 0;

    // Save available_units before removal if unit will be removed or decreased
    const parent_unit_subunits = (was_decreased && target_unit?.available_units)
      ? [...target_unit.available_units]
      : null;

    if (target_unit) {
      target_unit.quantity += delta;
      if (target_unit.quantity <= 0) {
        const index = selected_units.value.indexOf(target_unit);
        selected_units.value.splice(index, 1);
      }
    } else if (delta > 0) {
      selected_units.value.push({ ...unit, quantity: 1 });
    }

    structure.point_cost = (structure.point_cost || 0) + (unit.point_cost * delta);

    // If a parent unit was removed or decreased, check and remove excess subunits
    if (was_decreased && parent_unit_subunits && parent_unit_subunits.length > 0) {
      parent_unit_subunits.forEach(subunit => {
        const selected_subunit = selected_units.value.find(u => u.tech_name === subunit.tech_name);
        if (selected_subunit) {
          const new_max = max_quantity(subunit);
          const current_quantity = selected_subunit.quantity;

          if (current_quantity > new_max) {
            const excess = current_quantity - new_max;
            selected_subunit.quantity = new_max;
            structure.point_cost = (structure.point_cost || 0) - (subunit.point_cost * excess);

            if (selected_subunit.quantity <= 0) {
              const index = selected_units.value.indexOf(selected_subunit);
              selected_units.value.splice(index, 1);
            }
          }
        }
      });
    }
  }

  return {
    selected_units,
    selected_uniq_keys,
    is_subunit: (unit: UnitType) =>
      structure.hero?.available_units?.some(parent =>
        parent.available_units?.some(sub => sub.tech_name === unit.tech_name)
      ) ?? false,
    get_parent_unit: (unit: UnitType) =>
      structure.hero?.available_units?.find(parent =>
        parent.available_units?.some(sub => sub.tech_name === unit.tech_name)
      ) ?? null,
    get_unit_from_list: (unit: UnitType, list: UnitType[]) =>
      list.find(u => u.tech_name === unit.tech_name),
    max_quantity,
    available_quantity: (unit: UnitType) => {
      const selected = selected_units.value.find(u => u.tech_name === unit.tech_name);
      return max_quantity(unit) - (selected?.quantity || 0);
    },
    add_unit: (unit: UnitType) => update_unit_quantity(unit, 1),
    subtract_unit: (unit: UnitType) => {
      const target = selected_units.value.find(u => u.tech_name === unit.tech_name);
      if (!target || target.quantity <= 0) return false;
      update_unit_quantity(unit, -1);
      return true;
    },
    update_unit_quantity
  };
}
